import React from "react";
import Layout from "@/components/Layout";
import { useCart } from "@/context/CartContext";
import { formatCAD } from "@/lib/format";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";

export default function CartPage() {
  const { cart, update, remove, subtotal } = useCart();
  const nav = useNavigate();

  const lines = cart?.lines || [];

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="overline mb-2">Your bag</div>
        <h1 className="font-heading text-4xl md:text-5xl tracking-tight mb-8">Shopping cart</h1>

        {lines.length === 0 ? (
          <div className="surface p-16 text-center" data-testid="empty-cart">
            <ShoppingBag className="w-10 h-10 mx-auto text-[#8A8782] mb-4" strokeWidth={1.5} />
            <p className="text-[#5C5A56] mb-6">Your cart is empty.</p>
            <Link to="/discover" className="btn-primary">Browse products</Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-12 gap-8">
            <div className="md:col-span-8 space-y-4" data-testid="cart-lines">
              {lines.map((line, idx) => (
                <div key={idx} className="surface p-4 flex gap-4 items-center">
                  <img src={line.image} alt={line.name} className="w-24 h-24 object-cover" />
                  <div className="flex-1">
                    <div className="font-heading text-lg">{line.name}</div>
                    {line.custom_text && <div className="text-xs text-[#5C5A56] mt-1">Custom text: "{line.custom_text}"</div>}
                    {line.pod_payload && <div className="text-xs text-[#5C5A56] mt-1">POD design · {line.pod_payload.zone}</div>}
                    <div className="font-mono text-sm mt-2">{formatCAD(line.unit_price)}</div>
                  </div>
                  <div className="flex items-center border border-[#E5E3DD]">
                    <button onClick={() => update(idx, line.quantity - 1)} className="px-3 py-2" data-testid={`cart-minus-${idx}`}><Minus className="w-3 h-3" strokeWidth={1.5} /></button>
                    <span className="px-3 font-mono text-sm">{line.quantity}</span>
                    <button onClick={() => update(idx, line.quantity + 1)} className="px-3 py-2" data-testid={`cart-plus-${idx}`}><Plus className="w-3 h-3" strokeWidth={1.5} /></button>
                  </div>
                  <button onClick={() => remove(idx)} className="btn-ghost" data-testid={`cart-remove-${idx}`}><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                </div>
              ))}
            </div>
            <div className="md:col-span-4">
              <div className="surface p-6 sticky top-24" data-testid="cart-summary">
                <h3 className="font-heading text-xl mb-4">Order summary</h3>
                <Row label="Subtotal" value={formatCAD(subtotal)} />
                <Row label="Tax (HST estimate)" value={formatCAD(subtotal * 0.13)} />
                <Row label="Shipping" value={subtotal > 75 ? "Free" : formatCAD(9.99)} />
                <div className="border-t border-[#E5E3DD] my-3" />
                <div className="flex justify-between text-lg font-medium">
                  <span>Total</span>
                  <span className="font-mono">{formatCAD(subtotal + subtotal * 0.13 + (subtotal > 75 ? 0 : 9.99))}</span>
                </div>
                <button onClick={() => nav("/checkout")} className="btn-primary w-full mt-5" data-testid="checkout-btn">Proceed to checkout</button>
                <Link to="/discover" className="block text-center text-sm text-[#5C5A56] mt-3 hover:text-[#702A32]">Continue shopping</Link>
              </div>
            </div>
          </div>
        )}
      </section>
    </Layout>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-sm text-[#5C5A56] py-1.5">
      <span>{label}</span><span className="font-mono">{value}</span>
    </div>
  );
}
